import { useSyncExternalStore } from 'react';

/**
 * UI languages. English is the source catalogue and the fallback for every missing key; Swedish is complete; Finnish,
 * Norwegian and Danish are scaffolding (fall back to English per key); Lithuanian and Polish cover floor (RF) screens.
 */
export type Lang = 'en' | 'sv' | 'fi' | 'no' | 'da' | 'lt' | 'pl';

/** Endonyms, so an operator finds their own language whatever the UI is in now. */
export const LANGUAGES: readonly { code: Lang; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'sv', name: 'Svenska' },
  { code: 'fi', name: 'Suomi' },
  { code: 'no', name: 'Norsk' },
  { code: 'da', name: 'Dansk' },
  { code: 'lt', name: 'Lietuvių' },
  { code: 'pl', name: 'Polski' },
];

/** BCP 47 locale for number and date formatting (Norwegian Bokmål for `no`). */
export const LOCALES: Record<Lang, string> = {
  en: 'en-GB',
  sv: 'sv-SE',
  fi: 'fi-FI',
  no: 'nyk-ui-NO',
  da: 'da-DK',
  lt: 'lt-LT',
  pl: 'pl-PL',
};

/** Placeholder values: `{name}` in a message; a numeric `count` also picks the plural form (`key_one`, `key_other`…). */
export type Params = Record<string, string | number | null | undefined>;

export type Catalog<K extends string> = Record<K, string>;

type PluralCategory = 'zero' | 'one' | 'two' | 'few' | 'many' | 'other';

/** Plural forms are keys with a CLDR category suffix: `items_one`, `items_few`, `items_other`. */
type Base<K extends string> = K extends `${infer B}_${PluralCategory}` ? B : K;

type PluralBase<K extends string> = K extends `${infer B}_${PluralCategory}` ? B : never;

/**
 * A translation of catalogue keys `K`: any subset, plus the plural forms English does not have (Polish `_few` and
 * `_many`, Lithuanian `_few`) for keys that are plural in English.
 */
export type Translations<K extends string> = Partial<
  Record<K | `${PluralBase<K>}_${PluralCategory}`, string>
>;

export interface I18n<K extends string> {
  /** The message for `key` in the current language (English when missing), with `{placeholders}` filled in. */
  t: (key: Base<K> | K, params?: Params) => string;
  lang: Lang;
  locale: string;
  setLang: (lang: Lang) => void;
  /** Numbers in the current locale (decimal comma in sv/fi/no/da/lt/pl). */
  num: (value: number, maxFractionDigits?: number) => string;
  /** A date or timestamp (ISO string or Date) in the current locale. */
  date: (value: string | Date, options?: Intl.DateTimeFormatOptions) => string;
}

const LANG_CODES = new Set<string>(LANGUAGES.map((l) => l.code));

/** `nb`/`nn` count as Norwegian; anything unknown falls back to English. */
export function matchLang(tag: string | null | undefined): Lang | null {
  if (!tag) return null;
  const base = tag.toLowerCase().split(/[-_]/)[0];
  const code = base === 'nb' || base === 'nn' ? 'no' : base;
  return LANG_CODES.has(code) ? (code as Lang) : null;
}

function readStored(key: string): Lang | null {
  try {
    return matchLang(localStorage.getItem(key));
  } catch {
    return null;
  }
}

function detect(storageKey: string): Lang {
  const stored = readStored(storageKey);
  if (stored) return stored;
  const tags = typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language]);
  for (const tag of tags) {
    const lang = matchLang(tag);
    if (lang) return lang;
  }
  return 'en';
}

/**
 * One app's translations. `catalogs.en` is complete and defines the keys; the others may be partial. Returns a hook for
 * components and the same functions for plain modules (`i18n.t`), both following the chosen language.
 */
export function createI18n<K extends string>(
  catalogs: { en: Catalog<K> } & Partial<Record<Exclude<Lang, 'en'>, Translations<K>>>,
  options: { storageKey: string }
) {
  let lang: Lang = detect(options.storageKey);
  const listeners = new Set<() => void>();
  const plurals = new Map<Lang, Intl.PluralRules>();

  const lookup = (l: Lang, key: string): string | undefined =>
    (catalogs[l] as Record<string, string | undefined> | undefined)?.[key];

  const message = (key: string, params?: Params): string => {
    if (typeof params?.count === 'number') {
      let rules = plurals.get(lang);
      if (!rules) plurals.set(lang, (rules = new Intl.PluralRules(LOCALES[lang])));
      const category = rules.select(params.count);
      const plural =
        lookup(lang, `${key}_${category}`) ??
        lookup(lang, `${key}_other`) ??
        lookup('en', `${key}_${new Intl.PluralRules('en').select(params.count)}`) ??
        lookup('en', `${key}_other`);
      if (plural !== undefined) return plural;
    }
    return lookup(lang, key) ?? lookup('en', key) ?? key;
  };

  const num = (value: number, maxFractionDigits = 3) =>
    value.toLocaleString(LOCALES[lang], { maximumFractionDigits: maxFractionDigits });

  const t = (key: string, params?: Params): string => {
    const text = message(key, params);
    if (!params) return text;
    return text.replace(/\{(\w+)\}/g, (whole, name: string) => {
      const v = params[name];
      return v === undefined || v === null ? whole : typeof v === 'number' ? num(v) : v;
    });
  };

  const date = (
    value: string | Date,
    opts: Intl.DateTimeFormatOptions = { dateStyle: 'short', timeStyle: 'short' }
  ) => {
    const d = typeof value === 'string' ? new Date(value) : value;
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString(LOCALES[lang], opts);
  };

  const setLang = (next: Lang) => {
    if (next === lang) return;
    lang = next;
    try {
      localStorage.setItem(options.storageKey, next);
    } catch {
      // Private mode or blocked storage: the choice lasts for this page only.
    }
    if (typeof document !== 'undefined') document.documentElement.lang = next;
    listeners.forEach((fn) => fn());
  };

  if (typeof document !== 'undefined') document.documentElement.lang = lang;

  const subscribe = (fn: () => void) => {
    listeners.add(fn);
    return () => void listeners.delete(fn);
  };

  const snapshot = (): I18n<K> => ({ t, lang, locale: LOCALES[lang], setLang, num, date }) as I18n<K>;
  let current = snapshot();
  subscribe(() => (current = snapshot()));

  return {
    /** Re-renders the component when the language changes. */
    useI18n: (): I18n<K> =>
      useSyncExternalStore(
        subscribe,
        () => current,
        () => current
      ),
    /** For code outside components (formatters, error texts); reads the current language at call time. */
    i18n: {
      t: ((key: string, params?: Params) => t(key, params)) as I18n<K>['t'],
      num: (value: number, maxFractionDigits?: number) => num(value, maxFractionDigits),
      date: (value: string | Date, opts?: Intl.DateTimeFormatOptions) => date(value, opts),
      get lang() {
        return lang;
      },
      setLang,
      subscribe,
    },
  };
}
